import semver from 'semver';

/**
 * CDN manifest used when our own updates API is unreachable.
 *
 * Deliberately the bucket ORIGIN, not the edge cache: latest.json is the one
 * object we overwrite in place, and an edge-cached stale manifest can point
 * at artifacts that disagree with it. Installers are immutable per version,
 * so THEY download via the edge (see DEFAULT_UPDATES_ARTIFACT_BASE_URL).
 */
export const DEFAULT_UPDATES_FALLBACK_URL =
  'https://apiboo-releases.fra1.digitaloceanspaces.com/latest.json';

/**
 * Base the release installers download from: the CDN edge under our own
 * domain, so shipped clients don't bake the storage provider's hostname in.
 */
export const DEFAULT_UPDATES_ARTIFACT_BASE_URL = 'https://cdn.apiboo.com';

export function getUpdatesBaseUrl() {
  const base = process.env.REACT_APP_UPDATES_BASE_URL?.trim();
  return base ? base.replace(/\/$/, '') : '';
}

/**
 * Full URL of the legacy CDN manifest, used as a fallback when the API fails.
 * @returns {string}
 */
export function getUpdatesFallbackUrl() {
  const url = process.env.REACT_APP_UPDATES_FALLBACK_URL?.trim();
  return url || DEFAULT_UPDATES_FALLBACK_URL;
}

/**
 * Base the release artifacts live under. Decoupled from the manifest URL on
 * purpose: the manifest is fetched from the bucket origin (mutable object,
 * must never be stale), while installers are immutable per version and
 * download from the CDN edge under our own domain.
 * @returns {string}
 */
export function getUpdatesArtifactBaseUrl() {
  const url = process.env.REACT_APP_UPDATES_ARTIFACT_BASE_URL?.trim();
  return (url || DEFAULT_UPDATES_ARTIFACT_BASE_URL).replace(/\/$/, '');
}

/**
 * Today's UTC calendar day as "YYYY-MM-DD". The server buckets update-check
 * counts by UTC date, so the client cache must use the same boundaries.
 * @param {Date} [now]
 * @returns {string}
 */
export function utcDayStamp(now = new Date()) {
  return now.toISOString().slice(0, 10);
}

/**
 * The current UTC half-day as "YYYY-MM-DDT00" or "YYYY-MM-DDT12": the window
 * of ONE counted update check. Two windows a day (owner, 16.10.2026) so the
 * server sees each install's running version and last check twice a
 * day instead of once. The date part is still `utcDayStamp`, which is what
 * tells the second check of a day from the first (`isRepeatCheckToday`).
 * @param {Date} [now]
 * @returns {string}
 */
export function utcHalfDayStamp(now = new Date()) {
  return `${utcDayStamp(now)}T${now.getUTCHours() < 12 ? '00' : '12'}`;
}

/**
 * Whether a counted check stamped `ledger` already ran on today's UTC day. The
 * next counted check that day is then a REPEAT: the server still marks the
 * install as checked in, but leaves the anonymous one-per-device-per-day
 * `update_check` counter alone, so active devices are not counted twice.
 * @param {string|null} ledger  a `utcHalfDayStamp` (or a pre-16.10 `utcDayStamp`)
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isRepeatCheckToday(ledger, now = new Date()) {
  return typeof ledger === 'string' && ledger.slice(0, 10) === utcDayStamp(now);
}

/**
 * `{base}/latest?v=&os=&arch=[&repeat=1]` — the counted update-check endpoint.
 * @param {string} baseUrl
 * @param {{ version?: string, os?: string, arch?: string, repeat?: boolean }} [context]
 * @returns {string}
 */
export function buildLatestManifestUrl(baseUrl, context = {}) {
  const base = String(baseUrl || '').replace(/\/$/, '');
  const query = [
    `v=${encodeURIComponent(context.version || '')}`,
    `os=${encodeURIComponent(context.os || '')}`,
    `arch=${encodeURIComponent(context.arch || '')}`,
    // Only ever "1", never a counter or a time: anything more would let the
    // server tell devices apart (see the backend's UpdatesController).
    ...(context.repeat ? ['repeat=1'] : []),
  ].join('&');
  return `${base}/latest?${query}`;
}

/**
 * `{base}/manifest` — the UNCOUNTED twin of `/latest`. Same body, records
 * nothing. It exists so checks that are not "this device is alive today" stay
 * out of the active-device metric; user-initiated checks from Settings go here
 * so a curious user clicking five times is not five active devices.
 * @param {string} baseUrl
 * @returns {string}
 */
export function buildManifestUrl(baseUrl) {
  const base = String(baseUrl || '').replace(/\/$/, '');
  return `${base}/manifest`;
}

/** @typedef {'access_denied' | 'not_found' | 'timeout' | 'network' | 'invalid' | 'unknown'} UpdateErrorCode */

/**
 * @param {unknown} err
 * @returns {{ code: UpdateErrorCode, message: string }}
 */
export function classifyUpdateError(err) {
  const message = err?.message || String(err);
  if (/HTTP 403/.test(message)) {
    return {
      code: 'access_denied',
      message:
        'Update manifest is not publicly accessible (HTTP 403). Publish latest.json with public-read on the CDN bucket.',
    };
  }
  if (/HTTP 404/.test(message)) {
    return {
      code: 'not_found',
      message: 'Update manifest was not found on the server (HTTP 404).',
    };
  }
  if (/timed out/i.test(message)) {
    return { code: 'timeout', message: 'Update check timed out.' };
  }
  if (/Invalid update manifest JSON/.test(message)) {
    return { code: 'invalid', message: 'Update manifest is not valid JSON.' };
  }
  if (/fetch failed|network|Failed to fetch/i.test(message)) {
    return { code: 'network', message: 'Could not reach the update server.' };
  }
  return { code: 'unknown', message: message || 'Update check failed.' };
}

/**
 * @param {string} osPlatform
 * @param {string} cpuArch
 * @returns {string | null}
 */
export function resolvePlatformKey(osPlatform, cpuArch) {
  const platform = String(osPlatform || '').toLowerCase();
  const arch = String(cpuArch || '').toLowerCase();

  if (platform === 'macos' || platform === 'darwin') {
    if (arch === 'aarch64' || arch === 'arm64') return 'darwin-aarch64';
    if (arch === 'x86_64' || arch === 'x64') return 'darwin-x86_64';
    return null;
  }
  if (platform === 'windows') {
    if (arch === 'x86_64' || arch === 'x64') return 'windows-x86_64';
    return null;
  }
  if (platform === 'linux') {
    if (arch === 'x86_64' || arch === 'x64') return 'linux-x86_64';
    return null;
  }
  return null;
}

/**
 * @param {string} baseUrl
 * @param {string} artifactUrl
 * @returns {string}
 */
export function resolveArtifactUrl(baseUrl, artifactUrl) {
  if (!artifactUrl) return '';
  if (/^https?:\/\//i.test(artifactUrl)) return artifactUrl;
  const base = baseUrl.replace(/\/$/, '');
  const relative = artifactUrl.replace(/^\//, '');
  return `${base}/${relative}`;
}

/**
 * Whether a manifest is internally consistent enough to act on.
 *
 * `minSupportedVersion` has been parsed and carried around since the manifest
 * format was introduced and never once enforced. This closes that, in the only
 * direction the field can safely be read.
 *
 * The tempting reading — "the client must be at least `minSupportedVersion` to
 * update in place" — is unusable against the data we actually publish:
 * `scripts/generate-latest-json.js` sets `minSupportedVersion` to the release's
 * OWN version, so every client below the latest release (i.e. every client with
 * an update to install) would be refused. That reading would block 100% of
 * updates.
 *
 * What is left, and is genuinely useful, is a floor on the OFFER: a manifest
 * must not advertise a version its own publisher marks as unsupported. A
 * manifest rolled back to an old release — or assembled to point at one — trips
 * this whenever the floor was not rolled back with it.
 *
 * Note this is a CONSISTENCY check, not a security control: every field here is
 * attacker-controlled if the bucket is. The security control is in Rust
 * (`src-tauri/src/artifact_version.rs`), which compares the DOWNLOADED BYTES'
 * embedded version against the installed version and refuses anything that is
 * not strictly newer.
 *
 * @param {object} manifest
 * @returns {boolean}
 */
export function isManifestSelfConsistent(manifest) {
  const floor = manifest?.minSupportedVersion;
  if (!floor || !semver.valid(floor)) return true; // absent/garbage floor: no claim to check
  if (!manifest?.version || !semver.valid(manifest.version)) return false;
  return !semver.lt(manifest.version, floor);
}

/**
 * The version an artifact entry actually IS, which is not always the version
 * the manifest headlines.
 *
 * `scripts/generate-latest-json.js` carries a platform forward when only the
 * other platform was rebuilt — the documented macOS-then-Windows release runs
 * two builds, and between them one entry is legitimately older than the
 * release. The generator now stamps every entry with its own `version` so the
 * carried one cannot claim the new number; this reads it back.
 *
 * Falling back to the headline version keeps manifests published before the
 * field existed working exactly as they did.
 *
 * @param {object} manifest
 * @param {object | null} [artifact]
 * @returns {string}
 */
export function resolveArtifactVersion(manifest, artifact) {
  const own = artifact?.version;
  if (own && semver.valid(own)) return own;
  return manifest?.version || '';
}

/**
 * @param {string} currentVersion
 * @param {object} manifest
 * @param {string | null} dismissedVersion
 * @param {object | null} [artifact] the entry for THIS platform, when known.
 *   Omitted, the manifest's headline version is used — the pre-existing
 *   behaviour, and still correct for a manifest whose entries carry no version.
 * @returns {boolean}
 */
export function isUpdateAvailable(currentVersion, manifest, dismissedVersion, artifact = null) {
  if (!manifest?.version || !semver.valid(manifest.version)) return false;
  if (!semver.valid(currentVersion)) return false;
  if (!isManifestSelfConsistent(manifest)) return false;
  // What is on offer for this platform, not what the manifest headlines. A
  // carried-forward entry is not an update for anyone already past it, and
  // offering it anyway means downloading bytes the Rust anti-rollback check
  // then refuses.
  const offered = resolveArtifactVersion(manifest, artifact);
  if (!semver.valid(offered)) return false;
  if (!semver.gt(offered, currentVersion)) return false;
  if (dismissedVersion && semver.eq(offered, dismissedVersion)) return false;
  return true;
}

/**
 * User-facing message after a manual or automatic update check.
 * @param {import('../services/updateService').UpdateCheckResult | null | undefined} result
 * @param {(key: string, opts?: object) => string} t
 * @returns {{ variant: 'success' | 'error' | 'info' | 'warning', message: string }}
 */
export function describeUpdateCheckResult(result, t) {
  if (!result) {
    return { variant: 'error', message: t('settings.checkFailed') };
  }

  if (result.error) {
    if (result.errorCode === 'access_denied') {
      return { variant: 'error', message: t('settings.updatesAccessDenied') };
    }
    if (result.errorCode === 'not_found') {
      return { variant: 'error', message: t('settings.updatesNotFound') };
    }
    return {
      variant: 'error',
      message: t('settings.checkFailedDetail', { detail: result.error }),
    };
  }

  if (!result.latest) {
    return { variant: 'error', message: t('settings.updatesNotConfigured') };
  }

  if (result.updateAvailable) {
    // The version being OFFERED for this platform — see resolveArtifactVersion.
    // Naming the headline version here would promise a build the user is not
    // about to get whenever their platform's entry was carried forward.
    return {
      variant: 'info',
      message: t('settings.updateAvailableDetail', {
        version: resolveArtifactVersion(result.latest, result.artifact),
      }),
    };
  }

  const current = result.currentVersion;
  const latest = result.latest.version;
  if (
    latest &&
    semver.valid(latest) &&
    semver.valid(current) &&
    semver.gt(latest, current) &&
    !result.artifact
  ) {
    return {
      variant: 'warning',
      message: t('settings.noBuildForPlatform', { version: latest }),
    };
  }

  const displayVersion = current || latest || '';
  return {
    variant: 'success',
    message: t('settings.upToDateDetail', { version: displayVersion }),
  };
}
